import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { BookA, Target, ArrowUpRight, Trophy, Flame } from "lucide-react";
import { http } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { Progress } from "@/components/ui/progress";
import { BuddyAvatar } from "@/components/buddy/BuddyAvatar";
import { Confetti } from "@/components/Celebration";
import { speak } from "@/lib/buddy";

const GoalCheer = ({ g, onClose }) => {
  const { profile, buddy } = useProfile();
  const name = profile.name.split(" ")[0];
  const line = `Hooray ${name}! You reached your weekly goal of ${g.target} ${g.label}. ${g.streak > 1 ? ` That's ${g.streak} weeks in a row!` : ""}${g.streak_badge ? ` You earned the ${g.streak_badge} badge!` : ""} Your family is going to be so proud!`;
  useEffect(() => { speak(line, buddy?.voice).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return createPortal(
    <div className="pointer-events-auto" style={{ pointerEvents: "auto" }}>
      <Confetti />
      <div className="fixed inset-0 z-[80] pointer-events-auto grid place-items-center bg-[#2C4A3E]/40 backdrop-blur-sm" onClick={onClose} data-testid="goal-cheer-overlay">
        <div onClick={(e) => e.stopPropagation()} className="rise rounded-[2rem] bg-[#FDFBF7] border border-[#E6DEC3] shadow-2xl p-8 max-w-sm text-center">
          <div className="mx-auto w-24 h-24"><BuddyAvatar buddy={buddy} className="w-full h-full" /></div>
          <p className="font-display text-3xl text-[#2C4A3E] mt-4"><Trophy className="inline w-6 h-6 text-[#C87D53] mr-1" />Goal reached!</p>
          <p className="text-stone-600 mt-2">{line}</p>
          <p className="mt-4 inline-block px-4 py-2 rounded-full bg-[#FBE3B5] text-[#7A4A12] text-sm font-medium" data-testid="goal-cheer-badge">{g.streak_badge || "Weekly Goal Champion"}</p>
          <div className="mt-6"><button onClick={onClose} data-testid="goal-cheer-close" className="px-6 py-3 rounded-full bg-[#C87D53] text-white">Yay!</button></div>
        </div>
      </div>
    </div>, document.body);
};

export const MyGoal = () => {
  const [g, setG] = useState(null);
  const [cheer, setCheer] = useState(false);
  useEffect(() => { http.get("/goals/me").then((r) => { setG(r.data); setCheer(!!r.data?.cheer); }).catch(() => {}); }, []);
  if (!g) return null;
  const close = () => { setCheer(false); http.post("/goals/me/seen").catch(() => {}); };
  return (
    <section className="mt-8 rounded-3xl bg-[#FBF3E2] border border-[#E6DEC3] p-6 flex flex-wrap items-center gap-4" data-testid="my-goal-card">
      <div className="w-12 h-12 rounded-full grid place-items-center bg-[#F2D49B] text-[#5B3A1E]">{g.met ? <Trophy className="w-6 h-6" /> : <Target className="w-6 h-6" />}</div>
      <div className="flex-1 min-w-[200px]">
        <p className="text-xs uppercase tracking-widest text-stone-500">Family goal this week{g.set_by ? ` · from ${g.set_by.split(" ")[0]}` : ""}</p>
        <p className="font-display text-xl text-[#2C4A3E]" data-testid="my-goal-text">{g.target} {g.label}</p>
        <Progress value={Math.min(100, (100 * g.progress) / g.target)} className="h-2 mt-2" />
      </div>
      {g.streak > 0 && <span className="inline-flex items-center gap-1 text-sm text-[#8A3A12]" data-testid="my-goal-streak"><Flame className="w-4 h-4" /> {g.streak} week{g.streak > 1 ? "s" : ""} in a row</span>}
      <span className="text-sm font-medium text-[#2C4A3E]" data-testid="my-goal-progress">{g.met ? "Goal met!" : `${g.progress} / ${g.target}`}</span>
      {cheer && <GoalCheer g={g} onClose={close} />}
    </section>
  );
};

export const WordsLink = () => {
  const [n, setN] = useState(null);
  useEffect(() => { http.get("/words/me").then((r) => setN(r.data)).catch(() => {}); }, []);
  if (!n) return null;
  return (
    <Link to="/words" data-testid="home-words-link" className="mt-8 flex flex-wrap items-center gap-4 rounded-3xl p-6 bg-white/70 border border-[#E6DEC3] hover:border-[#2C4A3E] transition-colors">
      <BookA className="w-6 h-6 text-[#C87D53]" />
      <span className="flex-1"><b className="font-display text-xl block text-[#2C4A3E]">My Words</b>
        <span className="text-sm text-stone-600">{n.length ? `${n.length} saved · ${n.filter((w) => w.mastered).length} mastered. Practice them with your Buddy!` : "Long-press any word while reading to save it here."}</span></span>
      <ArrowUpRight className="w-5 h-5 text-[#2C4A3E]" />
    </Link>
  );
};
